(() => {
  if (customElements.get('aji-recipe-finder-controller')) return;
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const handle = value => normalize(value).replace(/ /g, '-');
  const foodRules = {
    chicken: /\b(chicken|poultry|pollo|wings?)\b/,
    meat: /\b(beef|pork|steak|ribs?|burger|burgers|anticuchos|lomo|bacon)\b/,
    seafood: /\b(seafood|fish|shrimp|prawns?|salmon|tuna|ceviche|tiradito|lobster|crab|scampi)\b/,
    vegetables: /\b(vegetables?|veggies|cauliflower|broccoli|avocado|salad|salads|quinoa|corn|potatoes?|sweet potato)\b/,
    pasta: /\b(pasta|spaghetti|linguine|noodles?|mac and cheese|macaroni|tallarines)\b/,
    breakfast: /\b(breakfast|eggs?|toast|scrambled|omelette|omelet)\b/,
    dips: /\b(dip|dips|dipping|aioli|vinaigrette|dressing|compound butter|marinade|salsa criolla)\b/
  };
  const cache = new Map();
  class Finder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.root = this.closest('[data-aji-recipe-finder]');
      this.embedded = this.root.hasAttribute('data-aji-home-recipes') || this.root.hasAttribute('data-aji-product-recipes');
      this.lockedSauce = ['amarillo', 'verde', 'rojo'].includes(this.root.dataset.lockedSauce) ? this.root.dataset.lockedSauce : '';
      const root = this.root;
      this.form = root.querySelector('[data-aji-recipe-search]');
      this.grid = root.querySelector('[data-aji-results]');
      this.status = root.querySelector('[data-aji-result-status]');
      this.strings = JSON.parse(root.querySelector('[data-aji-finder-strings]').textContent);
      this.base = new URL(root.dataset.blogUrl, location.origin);
      this.size = Math.max(1, Number(root.dataset.pageSize) || 9);
      this.visibleCount = 0;
      this.readURL();
      this.form.addEventListener('submit', event => { event.preventDefault(); this.update(); });
      this.form.addEventListener('change', event => {
        this.update();
        if (event.target.matches('[name=food]')) {
          const dropdown = event.target.closest('details'); dropdown.open = false;
          dropdown.querySelector('summary').focus({ preventScroll: true });
        }
      });
      this.onOutside = event => {
        const selected = event.target.closest('.aji-filter-dropdown');
        root.querySelectorAll('.aji-filter-dropdown[open]').forEach(dropdown => { if (dropdown !== selected) dropdown.open = false; });
      };
      this.onFilterKey = event => {
        if (event.key !== 'Escape') return;
        const dropdown = event.target.closest('.aji-filter-dropdown[open]');
        if (dropdown) { event.preventDefault(); dropdown.open = false; dropdown.querySelector('summary').focus(); }
      };
      document.addEventListener('click', this.onOutside);
      this.form.addEventListener('keydown', this.onFilterKey);
      this.form.querySelector('[data-aji-query]').addEventListener('input', () => {
        clearTimeout(this.timer); this.timer = setTimeout(() => this.update(false), 220);
      });
      root.querySelectorAll('[data-aji-clear]').forEach(button => button.addEventListener('click', () => {
        this.form.reset(); this.tagFilters = []; this.update();
      }));
      root.querySelector('[data-aji-retry]').addEventListener('click', () => this.load());
      this.more = root.querySelector('[data-aji-load-more]');
      this.sentinel = root.querySelector('[data-aji-scroll-sentinel]');
      this.more.addEventListener('click', () => this.appendMore(true));
      if (!this.embedded && typeof window.IntersectionObserver === 'function') {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.appendMore();
        }, { rootMargin: '350px 0px' });
      }
      this.onPop = () => { this.readURL(); this.visibleCount = 0; if (this.cards) this.render(); };
      addEventListener('popstate', this.onPop);
      this.form.querySelector('[data-aji-finder-controls]').hidden = false;
      this.load();
    }
    disconnectedCallback() {
      clearTimeout(this.timer); removeEventListener('popstate', this.onPop); document.removeEventListener('click', this.onOutside); this.form?.removeEventListener('keydown', this.onFilterKey); this.controller?.abort(); this.observer?.disconnect();
    }
    readURL() {
      const url = new URL(location.href);
      this.form.querySelector('[data-aji-query]').value = url.searchParams.get('recipe_q') || '';
      const sauce = this.lockedSauce || url.searchParams.get('sauce') || 'all';
      this.form.querySelectorAll('[name=sauce]').forEach(input => { input.checked = input.value === sauce; });
      if (!this.form.querySelector('[name=sauce]:checked')) this.form.querySelector('[name=sauce][value=all]').checked = true;
      const food = url.searchParams.get('food') || 'all';
      this.form.querySelectorAll('[name=food]').forEach(input => { input.checked = input.value === (Object.hasOwn(foodRules, food) ? food : 'all'); });
      this.syncFilterLabel();
      for (const key of ['quick', 'vegan', 'vegetarian', 'gluten_free']) this.form.querySelector(`[name=${key}]`).checked = url.searchParams.get(key) === '1';
      const suffix = url.pathname.split('/tagged/')[1] || '';
      this.tagFilters = suffix ? suffix.split('+').map(tag => handle(decodeURIComponent(tag))).filter(Boolean) : [];
    }
    syncFilterLabel() {
      const checked = this.form.querySelector('[name=food]:checked');
      this.form.querySelector('[data-aji-food-label]').textContent = !checked || checked.value === 'all' ? this.strings.food_category : checked.nextElementSibling.textContent;
    }
    state() {
      const data = new FormData(this.form);
      return { query: data.get('q').trim(), sauce: this.lockedSauce || data.get('sauce') || 'all', food: data.get('food') || 'all', quick: data.has('quick'), vegan: data.has('vegan'), vegetarian: data.has('vegetarian'), gluten_free: data.has('gluten_free') };
    }
    syncURL(push) {
      const state = this.state(); const url = new URL(location.href);
      if (!this.embedded) url.pathname = this.base.pathname.replace(/\/$/, '') + (this.tagFilters.length ? '/tagged/' + this.tagFilters.join('+') : '');
      for (const key of ['page', 'recipe_q', 'sauce', 'food', 'quick', 'vegan', 'vegetarian', 'gluten_free']) url.searchParams.delete(key);
      if (state.query) url.searchParams.set('recipe_q', state.query);
      if (state.sauce !== 'all') url.searchParams.set('sauce', state.sauce);
      if (state.food !== 'all') url.searchParams.set('food', state.food);
      for (const key of ['quick', 'vegan', 'vegetarian', 'gluten_free']) if (state[key]) url.searchParams.set(key, '1');
      if (url.href !== location.href) history[push ? 'pushState' : 'replaceState']({}, '', url);
    }
    update(push = true) { this.syncFilterLabel(); clearTimeout(this.timer); this.visibleCount = 0; this.syncURL(push); if (this.cards) this.render(); }
    async load() {
      if (this.loading) return;
      this.loading = true; this.root.setAttribute('aria-busy', 'true'); this.status.textContent = this.strings.loading;
      this.root.querySelector('[data-aji-finder-error]').hidden = true;
      this.controller = new AbortController();
      const timeout = setTimeout(() => this.controller.abort(), 30000);
      try {
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        const cacheKey = this.base.pathname + '|' + (preview || '');
        let cards = cache.get(cacheKey);
        if (!cards) {
          cards = []; let page = 1; let pages = 1; const urls = new Set();
          do {
            const url = new URL(this.base); url.searchParams.set('view', 'aji-recipe-index'); url.searchParams.set('page', String(page));
            if (preview) url.searchParams.set('preview_theme_id', preview);
            const response = await fetch(url, { credentials: 'same-origin', signal: this.controller.signal });
            if (!response.ok) throw new Error('Recipe request failed');
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const index = doc.querySelector('[data-aji-recipe-index]');
            if (!index || Number(index.dataset.page) !== page) throw new Error('Recipe index unavailable');
            pages = Number(index.dataset.pages) || 1;
            const content = index.querySelector('[data-aji-recipe-index-content]')?.content || index;
            for (const card of content.querySelectorAll('[data-aji-recipe-card]')) {
              if (urls.has(card.dataset.url)) continue;
              urls.add(card.dataset.url);
              const tags = JSON.parse(card.dataset.tags || '[]');
              cards.push({ element: card, title: normalize(card.dataset.title), search: normalize(card.dataset.search), classification: normalize(card.dataset.classification), tags: tags.map(normalize), handles: tags.map(handle), rawTags: tags, minutes: card.dataset.minutes === '' ? null : Number(card.dataset.minutes) });
            }
            page++;
          } while (page <= pages);
          cache.set(cacheKey, cards);
        }
        this.cards = cards;
        this.root.querySelector('[data-aji-server-pagination]')?.setAttribute('hidden', '');
        this.render();
      } catch (error) {
        if (this.isConnected) {
          this.status.textContent = this.strings.all_recipes;
          this.root.querySelector('[data-aji-finder-error]').hidden = false;
          this.root.querySelector('[data-aji-finder-controls]').hidden = true;
        }
      } finally {
        clearTimeout(timeout); this.loading = false; this.root.removeAttribute('aria-busy');
      }
    }
    render() {
      this.form.querySelector('[data-aji-finder-controls]').hidden = false;
      const state = this.state(); const words = normalize(state.query).split(' ').filter(Boolean);
      const matching = this.cards.filter(card =>
        words.every(word => card.search.includes(word)) &&
        (state.sauce === 'all' || card.classification.includes(state.sauce)) &&
        (state.food === 'all' || foodRules[state.food]?.test(card.classification)) &&
        (!state.quick || card.tags.includes('quick') || card.tags.includes('quick ideas') || (card.minutes !== null && card.minutes > 0 && card.minutes <= 30)) &&
        (!state.vegan || card.tags.includes('vegan')) &&
        (!state.vegetarian || card.tags.includes('vegetarian') || card.tags.includes('vegan')) &&
        (!state.gluten_free || card.tags.includes('gluten free')) &&
        this.tagFilters.every(tag => card.handles.includes(tag))
      );
      if (words.length) matching.sort((a, b) => words.filter(word => b.title.includes(word)).length - words.filter(word => a.title.includes(word)).length);
      this.matching = matching;
      this.visibleCount = 0;
      this.grid.replaceChildren();
      this.appendMore();
      this.status.textContent = matching.length === 1 ? this.strings.count_one : this.strings.count_other.replace('{count}', String(matching.length));
      this.root.querySelector('[data-aji-empty]').hidden = matching.length > 0;
      const active = !!(state.query || (!this.lockedSauce && state.sauce !== 'all') || state.food !== 'all' || state.quick || state.vegan || state.vegetarian || state.gluten_free || this.tagFilters.length);
      this.root.querySelector('.aji-finder-toolbar [data-aji-clear]').hidden = !active;
      const activeTags = this.root.querySelector('[data-aji-active-tags]'); activeTags.replaceChildren();
      for (const tag of this.tagFilters) {
        const raw = this.cards.flatMap(card => card.rawTags).find(rawTag => handle(rawTag) === tag) || tag.replaceAll('-', ' ');
        const button = document.createElement('button'); button.type = 'button'; button.textContent = raw + ' ×';
        button.setAttribute('aria-label', this.strings.remove_tag.replace('{tag}', raw));
        button.addEventListener('click', () => { this.tagFilters = this.tagFilters.filter(value => value !== tag); this.update(); }); activeTags.append(button);
      }
    }
    appendMore(manual = false) {
      if (!this.matching) return;
      const next = this.matching.slice(this.visibleCount, this.visibleCount + this.size);
      const fragment = document.createDocumentFragment();
      let firstLink;
      next.forEach(card => {
        const element = card.element.cloneNode(true);
        firstLink ||= element.querySelector('h2 a');
        fragment.append(element);
      });
      this.grid.append(fragment);
      this.visibleCount += next.length;
      const hasMore = this.visibleCount < this.matching.length;
      this.more.hidden = !hasMore;
      this.sentinel.hidden = !hasMore || this.embedded;
      const progress = this.root.querySelector('[data-aji-scroll-status]');
      progress.hidden = this.matching.length === 0;
      progress.textContent = (hasMore ? this.strings.showing : this.strings.end_results)
        .replace('{shown}', String(this.visibleCount)).replace('{count}', String(this.matching.length));
      this.observer?.disconnect();
      if (hasMore) requestAnimationFrame(() => {
        if (this.isConnected && !this.sentinel.hidden) this.observer?.observe(this.sentinel);
      });
      if (manual && firstLink) firstLink.focus({ preventScroll: true });
    }
  }
  customElements.define('aji-recipe-finder-controller', Finder);
  const enhance = root => {
    const sections = [...root.querySelectorAll('[data-aji-recipe-finder]')];
    if (root.matches?.('[data-aji-recipe-finder]')) sections.unshift(root);
    sections.forEach(section => { if (!section.querySelector('aji-recipe-finder-controller')) section.append(document.createElement('aji-recipe-finder-controller')); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => enhance(document)); else enhance(document);
  document.addEventListener('shopify:section:load', event => enhance(event.target));
})();
